import chartApi from "api/chart/chartApi";
import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import CopyWrapper from "components/Common/CopyWrapper";
import DropdownOption from "components/Common/DropdownOption";
import LabelWrapper from "components/Common/LabelWrapper";
import TooltipCustom from "components/Common/TooltipCustom";
import { useRealtimeTrade } from "components/Hooks/ws/useRealtimeTrade";
import { AnimatePresence, motion } from "framer-motion";
import { TRANSACTIONS_TYPE_OBJECT_COLOR, TRANSACTIONS_TYPE_OBJECT_LABEL, TRANSACTIONS_TYPE_OPTIONS } from "helpers/constans";
import { formatScientificNumber, formatTimeToAgo } from "helpers/format";
import moment from "moment-timezone";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { AutoSizer, Column, InfiniteLoader, Table, defaultTableRowRenderer } from "react-virtualized";
import "react-virtualized/styles.css";
import { Accordion, AccordionItem, Col, Collapse, Input, Row, Spinner } from "reactstrap";
import { RenderContentDateRange } from "./components/RenderContentDateRange";

const PAGE_SIZE = 20;

const Transactions = ({ pairAddress = '', getChartData, tokenSymbols = { symbol: ['', ''], decimal: ['4', '4'] } }: { pairAddress?: string, getChartData?: (data: any, typeShowPriceColumn: 'USD' | 'NATIVE') => void, tokenSymbols?: { symbol: string[], decimal: string[] } }) => {
  const { t, i18n } = useTranslation();
  const { subscribeLatestTransaction, unsubscribeLatestTransaction, subscribePairTransaction, unsubscribePairTransaction } = useRealtimeTrade();
  const { layoutModeType } = useSelector((state: any) => state.Layout);
  const isDark: boolean = (layoutModeType === "dark");

  const CHECKBOX_TRANSACTIONS_TYPE_OPTION_LANG = TRANSACTIONS_TYPE_OPTIONS.map((item) => ({ label: t(item.label), value: item.value }));

  const defaultStart = null;
  const defaultEnd = null;

  const [params, setParams] = useState<any>({
    limit: PAGE_SIZE,
    page: 1,
    pair_address: '',
    transactionType: CHECKBOX_TRANSACTIONS_TYPE_OPTION_LANG[0]?.value,
    fromDate: defaultStart ? `${moment(defaultStart).format('Y-MM-DD HH:mm')}` : '',
    toDate: defaultEnd ? `${moment(defaultEnd).format('Y-MM-DD HH:mm')}` : '',
    amountMin: 0,
    amountMax: 0,
    totalMin: 0,
    totalMax: 0,
    hasMore: false,
    cursor: ''
  });

  const pairAddressOldRef = useRef<any>('');

  const [typeShowPriceColumn, setTypeShowPriceColumn] = useState<'USD' | 'NATIVE'>('NATIVE');

  const [rows, setRows] = useState<any>([]);
  const [loadedRowsMap, setLoadedRowsMap] = useState<any>({});
  const [newRowIds, setNewRowIds] = useState<string[]>([]); // Track new rows for animation

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(true);

  const [makerFilter, setMakerFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<any>(CHECKBOX_TRANSACTIONS_TYPE_OPTION_LANG[0]);
  const [amount, setAmount] = useState<any>({ min: "", max: "" });
  const [total, setTotal] = useState<any>({ min: "", max: "" });
  const [dateRangeSearch, setDateRangeSearch] = useState<any[]>([defaultStart, defaultEnd]);
  const [startDate, endDate] = dateRangeSearch;

  const loadMoreRows = async ({ startIndex, stopIndex }: any) => {
    if (isLoading || !params?.hasMore) return;
    const newLoaded: any = {};
    for (let i = startIndex; i <= stopIndex; i++) {
      newLoaded[i] = true;
    }
    setLoadedRowsMap((prev: any) => ({ ...prev, ...newLoaded }));
    getLastedTransactions();
  };

  const isRowLoaded = ({ index }: any) => !!loadedRowsMap[index];

  const rowGetter = ({ index }: any) => {
    const row = rows[index];
    if (!row) {
      console.warn(`Row at index ${index} is undefined`);
      return {};
    }
    return row;
  };

  const getLastedTransactions = async (_params: any = {}) => {
    try {
      if (isLoading || (!_params?.pair_address && !params?.pair_address)) {
        return;
      }
      setIsLoading(true);
      const __params = { ...params, ..._params };
      if (__params?.page === 1) {
        setRows([]);
        setLoadedRowsMap({});
      }
      const res: any = await chartApi.getTransactionsV2(__params);
      if (!!res?.success) {
        const newRows: any = (res.data?.data || []).map((row: any) => ({
          ...row,
          id: row.transactionHash, // Ensure id is set
        }));
        if (__params?.page === 1) {
          setRows([...newRows]);
        } else {
          setRows((prev: any) => [...prev, ...newRows]);
        }
        const hasMore = !!res?.data?.has_next;
        setParams({ ...__params, hasMore: hasMore, page: __params?.page + Number(hasMore), cursor: res?.data?.next_cursor });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast(t(`${error}`), CONFIG_OPTION_TOAST_ERROR);
      console.error(error);
    }
  };

  const searchData = () => {
    if (isLoading) return;
    setRows([]);
    const newParams = {
      ...params,
      limit: PAGE_SIZE,
      transactionType: typeFilter?.value,
      walletAddress: makerFilter,
      page: 1,
      fromDate: startDate ? `${moment(startDate).format("Y-MM-DD HH:mm")}` : '',
      toDate: endDate ? `${moment(endDate).format("Y-MM-DD HH:mm")}` : '',
      amountMin: amount?.min,
      amountMax: amount?.max,
      totalMin: total?.min,
      totalMax: total?.max,
      hasMore: false,
      cursor: '',
    };
    getLastedTransactions(newParams);
  };

  const resetData = () => {
    if (isLoading) return;
    const newParams = {
      ...params,
      limit: PAGE_SIZE,
      page: 1,
      transactionType: CHECKBOX_TRANSACTIONS_TYPE_OPTION_LANG[0]?.value,
      fromDate: '',
      toDate: '',
      walletAddress: '',
      amountMin: 0,
      amountMax: 0,
      totalMin: 0,
      totalMax: 0,
      hasMore: false,
      cursor: '',
    };
    setRows([]);
    setMakerFilter("");
    setTypeFilter(CHECKBOX_TRANSACTIONS_TYPE_OPTION_LANG[0]);
    setAmount({ min: '', max: '' });
    setTotal({ min: '', max: '' });
    setDateRangeSearch([null, null]);
    getLastedTransactions(newParams);
  };

  useEffect(() => {
    if (pairAddress) {
      getLastedTransactions({
        page: 1,
        cursor: '',
        pair_address: pairAddress,
      });
    }
  }, [pairAddress]);

  useEffect(() => {
    if (pairAddress) {
      setTimeout(() => {
        if (pairAddressOldRef?.current) {
          unsubscribePairTransaction(pairAddressOldRef?.current);
        }
        pairAddressOldRef.current = pairAddress;
        subscribePairTransaction(pairAddress);
      }, 3000);
    }
  }, [pairAddress]);

  useEffect(() => {
    return () => {
      unsubscribeLatestTransaction();
      if (pairAddressOldRef?.current) {
        unsubscribePairTransaction(pairAddressOldRef?.current);
      }
    };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeLatestTransaction((data: any) => {
      const _data = data?.address;
      if (!_data?.transactionHash) {
        console.warn('WebSocket data missing transactionHash:', _data);
        return;
      }
      if (String(typeFilter?.value)?.includes(String(_data?.transactionType)) && String(_data?.pair) === String(pairAddress)) {
        setRows((prevRows: any) => {
          const newData = { ..._data, id: _data.transactionHash };
          setNewRowIds((prev) => [...prev, newData.id]);
          const updatedRows = [newData, ...prevRows];
          return updatedRows;
        });
      }
    });
    return unsubscribe;
  }, [subscribeLatestTransaction, typeFilter?.value, pairAddress]);


  // useEffect(() => {
  //   setInterval(() => {
  //     setRows((currentRows: any) => {
  //       const actions = ['sell', 'buy', 'addLiquidity', 'removeLiquidity'];
  //       const newData = [
  //         {
  //           ...currentRows[5],
  //           id: `${+ new Date()}`,
  //           transactionType: actions[Math.floor(Math.random() * actions.length)]
  //         },
  //       ];
  //       setNewRowIds((prev) => [...prev, newData[0].id]);
  //       const updatedRows = [...newData, ...currentRows];
  //       return updatedRows;
  //     });
  //   }, 3000);
  // }, []);

  useEffect(() => {
    if (getChartData) {
      getChartData(rows, typeShowPriceColumn);
    }
  }, [rows, typeShowPriceColumn]);

  return (
    <div>
      <div className="card text-gray-700 overflow-x-hidden">
        <Accordion id="default-accordion-example" flush open="" style={{ borderTopLeftRadius: 'var(--vz-card-border-radius)', borderTopRightRadius: 'var(--vz-card-border-radius)' }}>
          <AccordionItem>
            <h2 className="accordion-header" id="headingOne">
              <button
                className={`accordion-button custom py-2 px-[10px] ${!isOpenFilter ? 'collapsed' : ''}`}
                type="button"
                onClick={() => setIsOpenFilter((_prev) => !_prev)}
                style={{ cursor: "pointer" }}
              >
                <span className="font-semibold">{t('Trade History')}</span>
              </button>
            </h2>
            <Collapse isOpen={isOpenFilter} className="accordion-collapse" id="collapseOne">
              <div className="accordion-body py-2 px-2 pb-3 d-flex flex-column flex-lg-row justify-content-between align-items-center gap-3">
                <div className="flex-grow-1">
                  <Row>
                    <Col sm={6} md={6} xl={2} xxl={2} className="mt-3 mt-xl-2">
                      <LabelWrapper label={t("Type")}>
                        <DropdownOption
                          isMultiLang
                          name="type"
                          dataList={CHECKBOX_TRANSACTIONS_TYPE_OPTION_LANG}
                          initialValue={typeFilter || CHECKBOX_TRANSACTIONS_TYPE_OPTION_LANG?.[0]}
                          onChangeSelect={(event: any) => {
                            setTypeFilter(event);
                          }}
                          className="dropdown-status-rounded z-hight"
                          classNamePrefix="name-prefix"
                          labelTop="Type"
                        />
                      </LabelWrapper>
                    </Col>
                    <Col sm={6} md={6} xl={2} xxl={2} className="mt-3 mt-xl-2">
                      <LabelWrapper label={t("Maker")} isShow={true}>
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={`${t("Maker")}...`}
                          value={makerFilter}
                          onChange={(e) => setMakerFilter(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              searchData();
                            }
                          }}
                        />
                      </LabelWrapper>
                    </Col>
                    <Col sm={12} md={6} xl={4} xxl={4} className="mt-3 mt-xl-2 order-0 order-xl-0">
                      <LabelWrapper label={t("DateTime")} isShow={true}>
                        <RenderContentDateRange
                          startDate={startDate}
                          endDate={endDate}
                          setDateRangeSearch={setDateRangeSearch}
                        />
                      </LabelWrapper>
                    </Col>
                    <Col sm={6} md={6} xl={2} xxl={2} className="items-center mt-3 mt-xl-2">
                      <LabelWrapper label={t("Label_Total")} isShow={true}>
                        <div className="d-flex gap-2">
                          <Input
                            type="number"
                            className="form-control search"
                            placeholder={`${t("Label_Min")}`}
                            value={total?.min}
                            onChange={(e) =>
                              setTotal((_prev: any) => ({ ..._prev, min: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                          <p className="py-2">→</p>
                          <Input
                            type="number"
                            className="form-control search"
                            placeholder={`${t("Label_Max")}`}
                            value={total?.max}
                            onChange={(e) =>
                              setTotal((_prev: any) => ({ ..._prev, max: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </div>
                      </LabelWrapper>
                    </Col>
                    <Col sm={6} md={6} xl={2} xxl={2} className="mt-3 mt-xl-2">
                      <LabelWrapper label={t("Label_Amount")} isShow={true}>
                        <div className="d-flex gap-2">
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Label_Min")}`}
                            value={amount?.min}
                            onChange={(e) =>
                              setAmount((_prev: any) => ({ ..._prev, min: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                          <p className="py-2">→</p>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Label_Max")}`}
                            value={amount?.max}
                            onChange={(e) =>
                              setAmount((_prev: any) => ({ ..._prev, max: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </div>
                      </LabelWrapper>
                    </Col>
                  </Row>
                </div>
                <div className="d-flex gap-2 mt-2 mt-xl-2">
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary bg-[#009ef7] !hover:bg-[#0086d2] min-w-[100px]"
                      onClick={searchData}
                    >
                      <i className="ri-search-line align-bottom me-1"></i>{" "}
                      {t("Button Search")}
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary fs-14 bg-[#878a99] !hover:bg-[#737582] min-w-[90px]"
                      onClick={() => {
                        resetData();
                      }}
                    >
                      <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                      {t("Button Reset")}
                    </button>
                  </div>
                </div>
              </div>
            </Collapse>
          </AccordionItem>
        </Accordion>
        <div style={{ height: "400px" }} className="position-relative table-transaction-infinite-loader overflow-y-hidden overflow-x-auto">
          <InfiniteLoader
            isRowLoaded={isRowLoaded}
            loadMoreRows={loadMoreRows}
            rowCount={rows.length + Number(!!params?.hasMore)}
            threshold={5}
          >
            {({ onRowsRendered, registerChild }) => (
              <AutoSizer>
                {({ width, height }) => (
                  <Table
                    ref={registerChild}
                    width={width <= 1200 ? 1200 : width}
                    height={height}
                    headerHeight={34}
                    rowHeight={36}
                    rowCount={rows.length}
                    rowGetter={rowGetter}
                    onRowsRendered={onRowsRendered}
                    className="min-w-full text-[13px] text-left bg-gray-100"
                    rowRenderer={(props) => {
                      const { index, style, className } = props;
                      if (index > 0) {
                        // Header row: Let react-virtualized handle it
                        return defaultTableRowRenderer(props);
                      }
                      const row = rows[index];
                      if (!row) {
                        console.warn(`Row at index ${index} is undefined`);
                        return null;
                      }
                      const isNew = newRowIds.includes(row.id);
                      return (
                        <AnimatePresence>
                          <motion.div
                            key={row.id}
                            initial={isNew ? { opacity: 0, x: -100 } : true}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            style={style}
                            className={className}
                            onAnimationComplete={() => {
                              if (isNew) {
                                setNewRowIds((prev) => prev.filter((id) => id !== row.id));
                              }
                            }}
                          >
                            {defaultTableRowRenderer(props)}
                          </motion.div>
                        </AnimatePresence>
                      );
                    }}
                    rowClassName={({ index }: any) => {
                      if (index < 0) return '';
                      return `border-b transition-colors row-transition-bg-colors ${isDark ? "border-[#2a2d3e] hover:bg-[#1f2233]" : "border-gray-300 hover:bg-gray-100"
                        }`;
                    }}
                    headerClassName={`${isDark ? "text-gray-400" : "text-gray-700"}`}
                  >
                    <Column
                      label={t("Date")}
                      dataKey="timestamp"
                      className={`py-2 whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}
                      headerClassName="text-start text-capitalize"
                      width={160 + Number(width > 1200 ? ((width - 1200) / 8) : 0)}
                      cellRenderer={({ cellData }) => (
                        <span className="text-[13px]">
                          {formatTimeToAgo(cellData, t, false, 'MMM DD HH:mm:ss', `${i18n?.language}`)}
                        </span>
                      )}
                    />
                    <Column
                      label={t("Type")}
                      dataKey="transactionType"
                      width={100 + Number(width > 1200 ? ((width - 1200) / 8) : 0)}
                      headerClassName="text-center text-capitalize"
                      cellRenderer={({ cellData }) => (
                        <div
                          className={`font-medium leading-normal text-[13px] text-center ${TRANSACTIONS_TYPE_OBJECT_COLOR[`${cellData}`] || ''
                            }`}
                        >
                          {t(TRANSACTIONS_TYPE_OBJECT_LABEL[`${cellData}`] || '-')}
                        </div>
                      )}
                    />
                    <Column
                      label={t("Price")}
                      dataKey="baseTokenPriceUsd"
                      width={120 + Number(width > 1200 ? ((width - 1200) / 8) : 0)}
                      headerClassName="text-end text-capitalize"
                      cellRenderer={({ cellData, rowData }) => (
                        <div className="font-medium leading-normal text-[13px] text-end">
                          {['buy', 'sell']?.includes(rowData?.transactionType) ? (
                            typeShowPriceColumn === 'USD'
                              ? formatScientificNumber(rowData?.baseTokenPriceUsd, Number(rowData?.baseTokenPriceUsd) < 1 ? 6 : 4, '$')
                              : formatScientificNumber(rowData?.baseQuotePrice, 6, '')
                          ) : '-'}
                        </div>
                      )}
                      headerRenderer={({ label }) => (
                        <div
                          className="flex justify-content-end align-items-center space-x-2"
                          onClick={() => {
                            setTypeShowPriceColumn((_prev) => (_prev === 'USD' ? 'NATIVE' : 'USD'));
                          }}
                        >
                          <span>{label}</span>
                          <TooltipCustom
                            title={t('Toggle USD/Native Price')}
                            className="d-inline-block vertical-align-middle ms-1"
                            style={{ transform: 'translateY(2px)' }}
                          >
                            {typeShowPriceColumn === 'USD' ? (
                              <i className={`fs-16 bx bx-dollar-circle cursor-pointer ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-700 hover:text-gray-900'}`} />
                            ) : (
                              <i className={`fs-16 bx bx-bolt-circle cursor-pointer ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-700 hover:text-gray-900'}`} />
                            )}
                          </TooltipCustom>
                        </div>
                      )}
                    />
                    <Column
                      label={t("Label_Total")}
                      dataKey="totalValueUsd"
                      headerClassName="text-end text-capitalize"
                      width={120 + Number(width > 1200 ? ((width - 1200) / 8) : 0)}
                      cellRenderer={({ cellData, rowData }) => (
                        <div className="font-medium leading-normal text-[13px] text-end">
                          {['buy', 'sell']?.includes(rowData?.transactionType)
                            ? formatScientificNumber(cellData, Number(cellData) > 9 ? 2 : 4, '$')
                            : '-'}
                        </div>
                      )}
                    />
                    <Column
                      label={t('Amount') + ' ' + (tokenSymbols?.symbol?.[0] || t("Token Amount"))}
                      dataKey="baseTokenAmount"
                      headerClassName="text-end text-capitalize"
                      width={150 + Number(width > 1200 ? ((width - 1200) / 8) : 0)}
                      cellRenderer={({ cellData }) => (
                        <div className="font-medium leading-normal text-[13px] text-end">
                          {formatScientificNumber(
                            Number(cellData) > 100000 ? Number(cellData).toFixed(0) : cellData,
                            Number(cellData) < 1 ? 4 : 2,
                            ''
                          )}
                        </div>
                      )}
                    />
                    <Column
                      label={t('Amount') + ' ' + (tokenSymbols?.symbol?.[1] || t("Token Amount"))}
                      dataKey="quoteTokenAmount"
                      headerClassName="text-end text-capitalize"
                      width={150 + Number(width > 1200 ? ((width - 1200) / 8) : 0)}
                      cellRenderer={({ cellData }) => (
                        <div className="font-medium leading-normal text-[13px] text-end">
                          {formatScientificNumber(
                            Number(cellData) > 100000 ? Number(cellData).toFixed(0) : cellData,
                            Number(cellData) < 1 ? 4 : 2,
                            ''
                          )}
                        </div>
                      )}
                    />
                    <Column
                      label={t("Maker")}
                      dataKey="walletAddress"
                      headerClassName="text-end text-capitalize"
                      className={`text-end py-2 whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}
                      width={200 + Number(width > 1200 ? ((width - 1200) / 8) : 0)}
                      cellRenderer={({ cellData, rowData }) => (
                        <CopyWrapper isShow={true} contentCopy={rowData?.walletAddress}>
                          <span>
                            {rowData?.walletAddress?.slice(0, 8) + "..." + rowData?.walletAddress?.slice(-6)}
                          </span>
                        </CopyWrapper>
                      )}
                    />
                    <Column
                      label={t("TXN")}
                      dataKey="transactionHash"
                      width={200 + Number(width > 1200 ? ((width - 1200) / 8) : 0)}
                      headerClassName="text-end text-capitalize"
                      className={`text-end py-2 whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}
                      cellRenderer={({ cellData, rowData }) => (
                        <Link to={`https://solscan.io/tx/${rowData?.transactionHash}`} target="_blank" className="d-inline-block">
                          <CopyWrapper isShow={true} contentCopy={rowData?.transactionHash}>
                            <span>
                              {rowData?.transactionHash?.slice(0, 8) + "..." + rowData?.transactionHash?.slice(-6)}
                            </span>
                          </CopyWrapper>
                        </Link>
                      )}
                    />
                  </Table>
                )}
              </AutoSizer>
            )}
          </InfiniteLoader>
          {!!isLoading && (
            <div
              className="position-absolute d-flex justify-content-center align-items-center gap-2"
              style={{
                zIndex: 9,
                left: "50%",
                bottom: "0",
                transform: "translate(-50%, -50%)",
                color: "#374151",
                backgroundColor: "#98989833",
                padding: "10px",
                borderRadius: "5px",
              }}
            >
              <Spinner size="sm" style={{ width: "18px", height: "18px" }} />
              <span>{t("Loading")}....</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Transactions);